/**
 * Typed axios instance with a 402 response interceptor that maps the
 * subscription-tiers error codes to checkout-modal entry points and dispatches
 * `openWithEntry` (thunk). Centralises modal-open logic that used to live
 * inline in `chatSlice` and other thunks.
 *
 * Per design.md §"BE Contract", the error envelope is FastAPI-standard:
 *   { detail: { error_code: "<lowercase_snake_case>" } }
 * The interceptor reads `response.data.detail.error_code` (NOT the legacy
 * `response.data.error_code` path).
 *
 * Per design.md §"402 error-code routing matrix", this interceptor handles
 * ONLY `consultation_requires_counsel` (and the legacy `subscription_required`).
 * Other 402-coded errors are owned by:
 *   - `free_thread_limit_reached`        → WebsocketProvider
 *   - `consultation_credits_exhausted`   → LegalReviewShelf call-site (inline)
 *   - `free_user_message_limit_reached`  → WebsocketProvider (HTTP 402, NOT 400)
 *
 * Bootstrap order (App.js):
 *   1. createStore(...)
 *   2. registerApiClientStore(store)   // wires interceptor to dispatch
 *   3. axios calls now flow through `apiClient`
 *
 * Modules under test import the underscore-prefixed helpers (__mapError402,
 * __handle402); production code imports the default `apiClient` axios instance.
 */
import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type AxiosError,
} from "axios";
import * as Sentry from "@sentry/react";

import { openWithEntry, type CheckoutEntry } from "src/store/slices/checkoutFlowSlice";
import { Plan, SUBSCRIPTION_TIERS } from "src/domain/plan";

declare module "axios" {
  interface AxiosRequestConfig {
    /**
     * Opts this request out of the 402 checkout-modal interceptor below. For a page that renders its own
     * 402 state: the connector hand-off page (INH-5704) shows a plan view, and a purchase made in the
     * modal would write its own consultation lead without the matter typed on the page.
     */
    skipCheckoutModal?: boolean;
  }
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface MinimalStore {
  dispatch: (action: unknown) => unknown;
  getState: () => { onboarding?: { userMetadata?: { subscription_type?: string; id?: string } } };
}

interface FastApiDetail {
  error_code?: string;
}

interface ErrorResponseShape {
  status: number;
  data?: { detail?: FastApiDetail };
}

interface UserMetadataShape {
  subscription_type?: string;
  id?: string;
}

/**
 * BE wire codes (lowercase snake_case). Only the codes this interceptor
 * handles are listed here — see file header for the routing matrix.
 */
type Known402Code = "consultation_requires_counsel" | "subscription_required";

// ─── Constants ───────────────────────────────────────────────────────────────

export const INVERTED_GATING_SENTRY_TAG = "inverted_gating_suspect";

const ERROR_CODE_TO_ENTRY: Record<Known402Code, CheckoutEntry> = {
  consultation_requires_counsel: "consultation",
  subscription_required: "consultation",
};

/**
 * Tiers that should NEVER receive `consultation_requires_counsel` — they
 * already have (or can use) consultations. If a request from one of these
 * users hits this 402, BE branch logic has drifted: capture for triage.
 * Derived from the domain model: exactly the consultation-bearing plans.
 */
const COUNSEL_OR_ABOVE: ReadonlySet<string> = new Set(
  SUBSCRIPTION_TIERS.filter((tier) => Plan.from(tier).allowsConsultation()),
);

// ─── Store registration ──────────────────────────────────────────────────────

let registeredStore: MinimalStore | null = null;

export const registerApiClientStore = (store: MinimalStore): void => {
  registeredStore = store;
};

export const unregisterApiClientStore = (): void => {
  registeredStore = null;
};

// ─── Internals (exported for tests) ──────────────────────────────────────────

export const __mapError402 = (errorCode: string | undefined): CheckoutEntry | null => {
  if (!errorCode) return null;
  if (errorCode in ERROR_CODE_TO_ENTRY) {
    return ERROR_CODE_TO_ENTRY[errorCode as Known402Code];
  }
  return null;
};

/**
 * Pure-ish handler exported for unit testing. Production code path is via the
 * axios response interceptor below.
 */
export const __handle402 = (
  response: ErrorResponseShape,
  userMetadata: UserMetadataShape | null | undefined,
  threadId?: string | null
): void => {
  if (response.status !== 402) return;
  const errorCode = response.data?.detail?.error_code;
  const entry = __mapError402(errorCode);
  if (!entry) return;

  // Inverted-gating guard: Counsel or Unlimited users should NEVER receive
  // `consultation_requires_counsel`. If they do, BE branch logic is wrong
  // (or FE-side mapping has drifted). Capture for triage BEFORE opening the
  // modal so the alert correlates with the modal-mount event.
  if (
    errorCode === "consultation_requires_counsel" &&
    userMetadata?.subscription_type &&
    COUNSEL_OR_ABOVE.has(userMetadata.subscription_type)
  ) {
    Sentry.captureMessage(
      "Inverted gating: counsel/unlimited user blocked from consultation",
      {
        level: "error",
        tags: { [INVERTED_GATING_SENTRY_TAG]: "true" },
        extra: {
          error_code: errorCode,
          subscription_type: userMetadata.subscription_type,
          user_id: userMetadata.id ?? "unknown",
        },
      }
    );
  }

  if (registeredStore) {
    registeredStore.dispatch(
      openWithEntry({ entry, threadId: threadId ?? null })
    );
  }
};

// ─── Axios instance + interceptor ────────────────────────────────────────────

// Default to a localhost-rooted URL in non-prod so MSW (which intercepts at
// the http module level) can match handler patterns reliably. Production builds
// always set REACT_APP_BACKEND_BASE_ENDPOINT.
const BASE_URL =
  process.env.REACT_APP_BACKEND_BASE_ENDPOINT || "http://localhost";

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});

/**
 * The single home for the Authorization header previously copy-pasted at
 * every call site (roadmap Module 7). Pass a token when the caller resolved
 * one (e.g. through withAuthRetry); otherwise reads the stored session token.
 */
export const bearerAuthHeaders = (token?: string | null): Record<string, string> => ({
  Authorization: `Bearer ${token ?? localStorage.getItem("accessToken2")}`,
});

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.config?.skipCheckoutModal) return Promise.reject(error);
    const response = error.response;
    if (response && response.status === 402) {
      const userMetadata =
        registeredStore?.getState()?.onboarding?.userMetadata ?? null;
      __handle402(
        { status: response.status, data: response.data as { detail?: FastApiDetail } },
        userMetadata
      );
    }
    return Promise.reject(error);
  }
);

export default apiClient;
